// web/src/lib/quipPrefetch.ts
// Speculative quip prefetch. Fired on source-select in Create (masking the ~3s
// call behind the user's dwell); consumed once by /processing. Keyed on source
// identity + tone params so a swap / mode-flip / spice-change invalidates.
// A Firebase callable can't be truly aborted, so we abandon the RESULT by key —
// the "wasted call" is accepted (flash-lite = pennies).
import { generateQuips } from "./functions";
import type { Mode, Profanity } from "@/types/roast";
import type { DetectedSourceType } from "./createFlow";

const DEBOUNCE_MS = 300;
const MAX_RAW_BYTES = 8 * 1024 * 1024; // stay well under the callable payload limit

export function quipKey(file: File, mode: Mode, profanity: Profanity): string {
  return `${file.name}:${file.size}:${file.lastModified}:${mode}:${profanity}`;
}

interface Entry { key: string; promise: Promise<string[]>; }
let _entry: Entry | null = null;
let _debounce: ReturnType<typeof setTimeout> | null = null;

async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

async function fetchQuips(
  file: File, sourceType: DetectedSourceType, mode: Mode, profanity: Profanity,
): Promise<string[]> {
  if (file.size > MAX_RAW_BYTES) return [];
  try {
    const data = await fileToBase64(file);
    const mimeType = file.type || (sourceType === "photo" ? "image/jpeg" : "application/pdf");
    const res = await generateQuips({ data, mimeType, sourceType, mode, profanity, count: 20 });
    return res.data.quips ?? [];
  } catch {
    return []; // isolated — never throw into the UI
  }
}

/** Fire (debounced) a prefetch for this source+tone. Safe to call repeatedly. */
export function prefetchQuips(
  file: File, sourceType: DetectedSourceType, mode: Mode, profanity: Profanity,
): void {
  const key = quipKey(file, mode, profanity);
  if (_entry?.key === key) return; // already prefetching this exact source+tone
  if (_debounce) clearTimeout(_debounce);
  _debounce = setTimeout(() => {
    _debounce = null;
    _entry = { key, promise: fetchQuips(file, sourceType, mode, profanity) };
  }, DEBOUNCE_MS);
}

/** Discard any pending/cached prefetch (source removed or about to be replaced). */
export function cancelQuips(): void {
  if (_debounce) { clearTimeout(_debounce); _debounce = null; }
  _entry = null;
}

/** Consume the prefetched pool IF it matches the submitted source+tone; else fetch fresh. */
export function takeQuips(
  file: File, sourceType: DetectedSourceType, mode: Mode, profanity: Profanity,
): Promise<string[]> {
  const key = quipKey(file, mode, profanity);
  if (_entry?.key === key) return _entry.promise;
  return fetchQuips(file, sourceType, mode, profanity);
}
